/**
 * Admin Database Management Page
 *
 * Simple interface for viewing and managing database tables.
 * Uses basic HTML table for Phase 2 - can be enhanced in Phase 3 if needed.
 */

import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/Admin/AdminLayout';

const AdminDatabaseManagement = () => {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch available tables on mount
  useEffect(() => {
    fetchTables();
  }, []);

  // Fetch table data when selected table changes
  useEffect(() => {
    if (selectedTable) {
      fetchTableData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTable, currentPage, searchTerm]);

  const fetchTables = async () => {
    try {
      const response = await fetch('/api/admin/tables', {
        method: 'GET',
        credentials: 'include',
      });

      const data = await response.json();
      if (response.ok) {
        setTables(data.groupedTables || {});
      } else {
        setError(data.error || 'Failed to load tables');
      }
    } catch (err) {
      console.error('Fetch tables error:', err);
      setError(err.message);
    }
  };

  const fetchTableData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        table: selectedTable,
        page: currentPage.toString(),
        limit: '50',
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await fetch(`/api/admin/table-data?${params}`, {
        method: 'GET',
        credentials: 'include',
      });

      const data = await response.json();
      if (response.ok) {
        setTableData(data);
      } else {
        setError(data.error || 'Failed to load table data');
      }
    } catch (err) {
      console.error('Fetch table data error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on search
    fetchTableData();
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (tableData && currentPage < tableData.pagination.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <AdminLayout title="Database Management">
      <div className="space-y-6">
        {/* Table Selector */}
        <div className="bg-white shadow rounded-lg p-6">
          <label htmlFor="table-select" className="block text-sm font-medium text-gray-700 mb-2">
            Select Table
          </label>
          <select
            id="table-select"
            value={selectedTable}
            onChange={(e) => {
              setSelectedTable(e.target.value);
              setCurrentPage(1);
              setSearchTerm('');
            }}
            className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-daring-teal focus:border-daring-teal"
          >
            <option value="">-- Select a table --</option>
            {Object.entries(tables).map(([category, categoryTables]) => (
              <optgroup key={category} label={category}>
                {categoryTables.map(table => (
                  <option key={table.name} value={table.name}>
                    {table.displayName} ({table.recordCount} records)
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Table Data Display */}
        {selectedTable && (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            {/* Search and Controls */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <form onSubmit={handleSearch} className="flex space-x-2">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search..."
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-daring-teal focus:border-daring-teal"
                  />
                  <button
                    type="submit"
                    className="px-4 py-2 bg-daring-teal text-white rounded-md hover:bg-daring-navy focus:outline-none focus:ring-2 focus:ring-daring-teal transition-colors"
                  >
                    Search
                  </button>
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm('');
                        setCurrentPage(1);
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                    >
                      Clear
                    </button>
                  )}
                </form>

                {tableData && (
                  <div className="text-sm text-gray-600">
                    Showing {tableData.records.length} of {tableData.pagination.totalRecords} records
                  </div>
                )}
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-daring-teal mx-auto mb-2"></div>
                <p className="text-gray-600">Loading...</p>
              </div>
            )}

            {/* Table Data */}
            {!loading && tableData && tableData.records.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {tableData.columns.map(column => (
                        <th
                          key={column}
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tableData.records.map((record, index) => (
                      <tr key={index}>
                        {tableData.columns.map(column => (
                          <td
                            key={column}
                            className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                          >
                            {record[column] !== null && record[column] !== undefined
                              ? String(record[column]).substring(0, 100)
                              : '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* No Records */}
            {!loading && tableData && tableData.records.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                No records found
              </div>
            )}

            {/* Pagination */}
            {tableData && tableData.pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    Page {tableData.pagination.page} of {tableData.pagination.totalPages}
                  </div>
                  <div className="space-x-2">
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === tableData.pagination.totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDatabaseManagement;
